'use client';

import { CircleCheck } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

export default function WhyChooseSection() {
  const benefits = [
    {
      title: 'Stay compliant, automatically',
      description:
        'PAYE, Pension, NHF and NSITF for Nigeria; SSNIT and Tier 2/3 for Ghana updated as rules change.',
    },
    {
      title: 'Pay through multiple options',
      description:
        'Disburse via a secured, encrypted platform integrated with the rails you already use.',
    },
    {
      title: 'Self-service for employees',
      description:
        'Payslips, earnings history and personal data always one login away.',
    },
    {
      title: 'Tight internal controls',
      description:
        'Role-based approvals keep finance, HR and leadership on the same page.',
    },
    {
      title: 'Decision made',
      description:
        'Reduce repetitive work and process payroll accurately without relying on spreadsheets.',
    },
    {
      title: 'Insightful payroll reports',
      description:
        'Get clear, up-to-date payroll insights and reports whenever your business needs them.',
    },
  ];

  const scrollRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

    useEffect(() => {
    const element = scrollRef.current;

    if (!element) return;

    const autoScroll = () => {
      if (!element || isDragging) {
        animationRef.current = requestAnimationFrame(autoScroll);
        return;
      }

      // Carousel speed
      element.scrollLeft += 0.9;

      const halfWidth = element.scrollWidth / 2;

      if (element.scrollLeft >= halfWidth) {
        element.scrollLeft -= halfWidth;
      }

      animationRef.current = requestAnimationFrame(autoScroll);
    };

    animationRef.current = requestAnimationFrame(autoScroll);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isDragging]);

  /*
   * Mouse wheel
   * Allows the user to manually move the carousel
   * without stopping the automatic carousel permanently.
   */
  const handleWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    const element = event.currentTarget;

    if (element.scrollWidth > element.clientWidth) {
      event.preventDefault();
      element.scrollLeft += event.deltaY;
    }
  };

  /*
   * Mouse drag - start
   */
  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    const element = scrollRef.current;

    if (!element) return;

    setIsDragging(true);

    setStartX(event.pageX - element.offsetLeft);
    setScrollLeft(element.scrollLeft);

    element.style.cursor = 'grabbing';
  };

  /*
   * Mouse drag - move
   */
  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const element = scrollRef.current;

    if (!isDragging || !element) return;

    event.preventDefault();

    const x = event.pageX - element.offsetLeft;
    const walk = (x - startX) * 1.5;

    element.scrollLeft = scrollLeft - walk;
  };

  /*
   * Mouse drag - end
   */
  const handleMouseUp = () => {
    setIsDragging(false);

    if (scrollRef.current) {
      scrollRef.current.style.cursor = 'grab';
    }
  };

  /*
   * Mouse leaves carousel
   */
  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);

      if (scrollRef.current) {
        scrollRef.current.style.cursor = 'grab';
      }
    }
  };

  return (
    <section className="benefits-section" id="benefits">
      <div className="benefits-container">

        {/* Header */}
        <div className="benefits-header">
          <p className="benefits-label">
            WHY TEAMS CHOOSE AVETIUM PAYROLL
          </p>

          <h2 className="benefits-title">
            Payroll that works for your team,{' '}
            <span>not against it.</span>
          </h2>

          <p className="benefits-subtitle">
            Payroll is more than a monthly task — it's a promise to your people.
            <br className="desktop-break" />
            Avetium helps you keep it, every time.
          </p>
        </div>

        {/* Cards */}
        <div className="benefits-grid-wrapper">
          <div className="benefits-decoration" />

          <div
            className="benefits-scroll"
            ref={scrollRef}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onTouchStart={() => setIsDragging(true)}
            onTouchEnd={() => setIsDragging(false)}
          >
            <div className="benefits-grid">
              {[...benefits, ...benefits].map((benefit, index) => (
                <div
                  className="benefit-card"
                  key={`${benefit.title}-${index}`}
                >
                  <div className="benefit-icon">
                    <CircleCheck size={23} strokeWidth={2.8} />
                  </div>

                  <div className="benefit-content">
                    <h3>{benefit.title}</h3>
                    <p>{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}